import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, BookOpen, ArrowRight, Bell, BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Parcours() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [outlines, setOutlines] = useState({});
  const [waitlist, setWaitlist] = useState([]);

  const loadWaitlist = useCallback(() => {
    if (!user) return;
    api.get("/waitlist").then(({ data }) => setWaitlist(data.levels)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/courses", { params: { level: 1 } }).then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/levels").then(({ data }) => setLevels(data)).catch(() => {});
    api.get("/levels/outlines").then(({ data }) => setOutlines(data)).catch(() => {});
    loadWaitlist();
  }, [loadWaitlist]);

  const toggleWaitlist = async (levelId) => {
    if (!user) { navigate("/auth"); return; }
    if (waitlist.includes(levelId)) {
      await api.delete(`/waitlist/${levelId}`);
      toast.info("Retiré·e de la liste d'attente");
    } else {
      await api.post(`/waitlist/${levelId}`);
      toast.success("Inscrit·e ! Vous serez averti·e dès l'ouverture des inscriptions");
    }
    loadWaitlist();
  };

  const level1 = levels.find((l) => l.level_id === 1);

  return (
    <main className="max-w-7xl mx-auto px-6 py-16 relative z-10" data-testid="parcours-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Chimen Nanm</p>
      <h1 className="text-4xl sm:text-5xl font-serif-display tracking-tight">{level1?.name || "Niveau 1 — Découverte"}</h1>
      <p className="text-foreground/70 mt-4 max-w-2xl leading-relaxed">{level1?.description}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
        {courses.map((c, i) => (
          <Link to={`/cours/${c.course_id}`} key={c.course_id}
            className="card-lift group bg-card border border-primary/15 rounded-lg overflow-hidden flex flex-col"
            data-testid={`course-card-${c.course_id}`}
            style={{ animationDelay: `${i * 80}ms` }}>
            <div className="relative h-44 overflow-hidden">
              <img src={c.image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              {c.free_course && (
                <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1 rounded-full" data-testid={`course-free-badge-${c.course_id}`}>
                  {t("common.freeTag")}
                </span>
              )}
              <span className="absolute bottom-3 left-4 font-serif-display text-4xl text-primary/60">{String(c.order).padStart(2, "0")}</span>
            </div>
            <div className="p-6 flex flex-col flex-1">
              <h3 className="text-xl font-serif-display text-foreground group-hover:text-primary transition-colors duration-200">{c.title}</h3>
              <p className="text-sm text-foreground/60 mt-3 leading-relaxed flex-1">{c.summary}</p>
              <div className="flex items-center justify-between mt-5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock size={13} /> {c.duration}</span>
                <span className="flex items-center gap-1.5"><BookOpen size={13} /> {c.module_count} {t("levels.courses") === "cours" ? "modules" : "modules"}</span>
                <ArrowRight size={14} className="text-primary" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Coming levels + waitlist */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        {levels.filter((l) => l.status === "coming_soon").map((l) => {
          const outline = outlines[String(l.level_id)];
          const joined = waitlist.includes(l.level_id);
          return (
            <div key={l.level_id} className="border border-dashed border-primary/25 rounded-lg p-8 flex flex-col" data-testid={`coming-level-${l.level_id}`}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("levels.coming")} · {l.price_display}</p>
              <h3 className="text-2xl font-serif-display mt-2 text-foreground/80">{l.name}</h3>
              <p className="text-sm text-foreground/55 mt-3">{l.description}</p>
              {outline && (
                <ul className="mt-5 space-y-1.5 flex-1" data-testid={`outline-level-${l.level_id}`}>
                  {outline.sessions.map((s) => (
                    <li key={s} className="text-xs text-foreground/60 flex items-start gap-2 leading-relaxed">
                      <CheckCircle2 size={12} className="text-primary/50 mt-0.5 shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={() => toggleWaitlist(l.level_id)}
                className={`mt-6 inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm transition-colors duration-200 ${joined ? "bg-primary/10 text-primary border border-primary/40" : "bg-primary text-primary-foreground hover:bg-[#16528A]"}`}
                data-testid={`waitlist-btn-${l.level_id}`}>
                {joined ? <BellRing size={15} /> : <Bell size={15} />}
                {joined ? "Sur la liste d'attente ✓" : "Rejoindre la liste d'attente"}
              </button>
            </div>
          );
        })}
      </div>
    </main>
  );
}
